import { decodePolyMerges, type PolyMergeAccounting } from './mergeAccounting';
// ── Browser → relay HTTP helper for Polymarket orders ───────────
//
// Thin wrapper around `/api/polymarket/orders` (the trader-facing relay
// route that proxies to the org's stored Polymarket credential). Use
// this for org-key mode trading. The browser-side `placePolyOrder` in
// ./client.ts is still the right path for the personal-key flow that
// signs orders with a localStorage-stored EVM private key.
//
// The relay returns a discriminating `code` on 404 so we can give the
// user actionable copy instead of "Polymarket trading is unavailable":
//   NOT_IN_ORG          → "You are not part of an org…"
//   PERSONAL_KEY_MODE   → "Personal-key mode — flip useOrgKeys…"
//   NO_POLY_CRED        → "Org has no Polymarket key — ask an admin…"
//
// Order-attempt failures (the 502 lane) also carry a code as of #549 — see
// PolyOrderOutcomeCode — and their `error` string is a SAFE sentence, not the
// underlying exception. Raw SDK/JS exception text is never forwarded to a
// trader; it stays on the audit row and in the relay log. Only a response with
// no code at all (a transport failure before the relay answered) falls through
// to the raw string, which `explainPolyError` then decodes or passes along.

import { apiFetch } from '@/stores/authStore';
import { isOrgBalanceHiddenBody } from '@/lib/orgBalanceHidden';
import { beginPolyAttempt, type PolyAttempt } from '../infra/polyAttemptJournal';
import { POLY_ATTEMPT_HEADER } from '../../../relay/src/diagnostics/polyAttempt';
import { sourceConfirmationError } from '../trading/sourceConfirmation';
import {
  isLimitRejectionPayload,
  type LimitRejectionBreach,
  type LimitRejectionCode,
} from '@/lib/limits';

export type PolyRelayUnavailableCode =
  | 'NOT_IN_ORG'
  | 'PERSONAL_KEY_MODE'
  | 'NO_POLY_CRED'
  | 'ONLY_LEGACY_EOA_CRED';

/** Advisory context on successful persisted-ledger reads (#427). Historical
 * data remains readable even when trading credentials are unavailable. Null
 * means the context check succeeded with a usable org credential; a code says
 * why it did not. CONTEXT_CHECK_FAILED keeps an unexpected check failure from
 * being mislabeled as healthy without discarding the ledger payload. */
export type PolyReadCredentialContext =
  | PolyRelayUnavailableCode
  | 'CONTEXT_CHECK_FAILED'
  | null;

/**
 * Order-attempt outcomes that are NOT a config problem (#549).
 *
 * Separate from {@link PolyRelayUnavailableCode} because these say nothing
 * about how the org is set up — they describe what happened to one order
 * attempt, and above all WHETHER IT WAS SUBMITTED. The relay derives that from
 * whether the CLOB post had started, so the distinction is observed rather than
 * guessed:
 *
 *   POLY_MARKET_METADATA_UNAVAILABLE — the venue would not tell us the market's
 *     tick, so nothing was built, signed or posted. Provably pre-submit.
 *   POLY_ORDER_PREP_FAILED — something else threw before the post began. Also
 *     provably pre-submit, but with no cause we can name for a trader.
 *   POLY_ORDER_STATE_UNKNOWN — the post had begun when it failed. We cannot say
 *     whether the CLOB accepted it, and must not pretend either way.
 */
export type PolyOrderOutcomeCode =
  | 'POLY_MARKET_METADATA_UNAVAILABLE'
  | 'POLY_ORDER_PREP_FAILED'
  | 'POLY_ORDER_STATE_UNKNOWN';

export type PolyRelayOrderErrorCode =
  | PolyRelayUnavailableCode
  | PolyOrderOutcomeCode
  | LimitRejectionCode
  | 'POLYMARKET_GEOBLOCK';

export interface PlacePolyOrderRelayArgs {
  tokenId: string;
  action: 'buy' | 'sell';
  priceCents: number;
  contracts: number;
  postOnly?: boolean;
  tickSize?: string;
  negRisk?: boolean;
  /** Unix seconds. When > 0, the relay submits as GTD and the CLOB
   *  auto-cancels at this time; omit or 0 → GTC. */
  expirationTs?: number;
  /** 'FAK' (IOC): fill whatever crosses at the limit price, cancel the
   *  remainder. Used by the close path; mutually exclusive with
   *  postOnly/expirationTs. */
  timeInForce?: 'FAK';
}

export type PlacePolyOrderRelayResult =
  | { ok: true; result: unknown }
  | {
      ok: false;
      status: number;
      error: string;
      code?: PolyRelayOrderErrorCode;
      /** Present when the relay rejected the order at the limit chokepoint
       *  (status 403, code LIMIT_*). Lets UIs render a richer "would exceed
       *  your cap" message than the bare error string. */
      breach?: LimitRejectionBreach;
    };

/**
 * POST to `/api/polymarket/orders`. Returns a discriminated union so
 * callers don't have to throw/catch to distinguish "Polymarket trading
 * isn't available right now (here's why)" from "the order itself was
 * rejected."
 */
export async function placePolyOrderViaRelay(
  args: PlacePolyOrderRelayArgs,
  diagnostic: PolyAttempt = beginPolyAttempt(args.tokenId),
): Promise<PlacePolyOrderRelayResult> {
  const sourceError = sourceConfirmationError({ tokenId: args.tokenId });
  if (sourceError) { diagnostic?.blocked(); return { ok: false, status: 409, error: sourceError }; }
  let res: Response;
  try {
    diagnostic?.dispatched();
    res = await apiFetch('/api/polymarket/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(diagnostic ? { [POLY_ATTEMPT_HEADER]: diagnostic.id } : {}) },
      body: JSON.stringify(args),
    });
  } catch (err) {
    diagnostic?.unknown();
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  diagnostic?.response(res.status);
  let body: { result?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    // Body wasn't JSON — leave body empty and fall through to status-based error.
  }

  if (res.ok) {
    return { ok: true, result: body.result };
  }

  // Limit rejection (403 from the relay chokepoint) carries a structured
  // breach payload; surface it on the result so UIs can render richer text.
  if (res.status === 403 && isLimitRejectionPayload(body)) {
    return {
      ok: false,
      status: res.status,
      error: body.error,
      code: body.code,
      breach: body.breach,
    };
  }

  // ⚠️ This is an ALLOWLIST, and anything missing from it is silently dropped
  // to `undefined` — which sends the caller down the raw-error fallback. A new
  // relay code that is not listed here is a no-op, however correct the relay
  // side is. #549's three outcome codes are listed for exactly that reason.
  const code =
    body.code === 'NOT_IN_ORG' ||
    body.code === 'PERSONAL_KEY_MODE' ||
    body.code === 'NO_POLY_CRED' ||
    body.code === 'ONLY_LEGACY_EOA_CRED' ||
    body.code === 'POLYMARKET_GEOBLOCK' ||
    body.code === 'POLY_MARKET_METADATA_UNAVAILABLE' ||
    body.code === 'POLY_ORDER_PREP_FAILED' ||
    body.code === 'POLY_ORDER_STATE_UNKNOWN'
      ? body.code
      : undefined;

  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

/**
 * Map an unavailable-trading code into actionable user copy. Returns
 * null for unknown codes so the caller can fall back to the raw error
 * message. The strings are user-facing — keep them short and concrete.
 */
export function describePolyRelayUnavailable(code: PolyRelayUnavailableCode): string {
  switch (code) {
    case 'NOT_IN_ORG':
      return 'Polymarket org trading requires you to be in an organization. Ask an admin to add you.';
    case 'PERSONAL_KEY_MODE':
      return 'Polymarket relay trading is only available in org-key mode. Switch in Settings.';
    case 'NO_POLY_CRED':
      return 'Your org does not have a Polymarket key configured. Ask an admin or manager to add one in Manager → Org Polymarket Key.';
    case 'ONLY_LEGACY_EOA_CRED':
      return 'Your org\'s only Polymarket keys are legacy EOA (type 0), which Polymarket no longer accepts. An admin must re-register the key with its account\'s signature type + funder (see the guide in Org Settings → Add Key).';
  }
}

/**
 * Copy for an order ATTEMPT outcome (#549). Sibling of
 * {@link describePolyRelayUnavailable} — kept separate for the same reason the
 * code types are: these turn on whether the order was submitted, not on how the
 * org is configured.
 *
 * The first two say "No order was submitted" and mean it. The relay only emits
 * them when the CLOB post had not begun, so the claim is derived from the
 * observed submission phase rather than assumed from the error looking harmless.
 *
 * The third deliberately promises nothing and sends the trader to look. Its
 * shape is lifted from the house UNVERIFIED vocabulary already used on the
 * close path (`closePosition.ts`: "the position may be fully open, partly
 * closed, or closed … do NOT assume either way") rather than invented here, so
 * the two surfaces sound like one product.
 */
export function describePolyOrderOutcome(code: PolyOrderOutcomeCode): string {
  switch (code) {
    case 'POLY_MARKET_METADATA_UNAVAILABLE':
      // Wording prescribed by #549.
      return 'Polymarket market details are temporarily unavailable. No order was submitted. Please retry; if it persists, report the market and time.';
    case 'POLY_ORDER_PREP_FAILED':
      // Deliberately does NOT say "please retry", unlike the metadata case.
      // This code covers deterministic pre-submit throws too — the SDK's own
      // `invalid tick size (…), minimum for the market is …` and `invalid price
      // (…)` guards among them — where an identical retry fails identically.
      // Promising a retry would send the trader in a circle.
      return 'Polymarket order preparation failed and no order was submitted. A retry may not help — report the market and time if it happens again.';
    case 'POLY_ORDER_STATE_UNKNOWN':
      return 'ORDER UNCONFIRMED: Polymarket did not confirm this order, and it may or may not have been accepted. Check Open Orders before retrying — do NOT assume either way.';
  }
}

// ── Open orders read + cancel ──────────────────────────────────
// The org's CLOB client lives on the relay; these mirror the
// place-order helper above for the rest of the order lifecycle.

/** Shape the SDK returns on getOpenOrders — fields are loose since
 *  CLOB's response is not strictly typed in our SDK version. */
export interface PolyRelayOpenOrder {
  id?: string;
  orderID?: string;
  orderId?: string;
  market?: string;
  asset_id?: string;
  side?: string;
  price?: number | string;
  size?: number | string;
  original_size?: number | string;
  size_matched?: number | string;
  timestamp?: number | string;
  [key: string]: unknown;
}

/** Remaining (unmatched) contracts on a CLOB open order:
 *  original_size − size_matched. The CLOB's open-order shape has no `size`
 *  field (#1397: `@polymarket/clob-client-v2` 1.0.6 `OpenOrder`); a positive
 *  legacy `size` is still read first, as the remaining. Null when there is
 *  no positive `size` and either original_size or size_matched won't parse —
 *  callers must not guess. The one shared derivation for the replace flow
 *  and every row renderer. CLOB sizes are fractional, and each display picks
 *  its own precision: the cards and side rail round to whole numbers (#144),
 *  Trader Detail rounds to three decimals, Game Detail keeps two (#1397). */
export function polyOpenOrderRemaining(order: PolyRelayOpenOrder): number | null {
  const toNumber = (v: unknown): number => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') return Number.parseFloat(v);
    return NaN;
  };
  const size = toNumber(order.size);
  if (Number.isFinite(size) && size > 0) return size;
  const orig = toNumber(order.original_size);
  const matched = toNumber(order.size_matched);
  if (Number.isFinite(orig) && Number.isFinite(matched)) return Math.max(0, orig - matched);
  return null;
}

/** Fail-closed guard for 2xx list payloads: the body must carry a well-formed
 *  array of row objects. Anything else — HTML masquerading as a 200, `{}`, an
 *  error envelope, null/scalar rows — is a FAILED READ, never an empty book.
 *  Port of the accountStore parseOrdersSnapshot contract (#230/#238 class). */
function isRowObjectArray(rows: unknown): rows is Record<string, unknown>[] {
  return (
    Array.isArray(rows) &&
    rows.every((row) => !!row && typeof row === 'object' && !Array.isArray(row))
  );
}

export type ListPolyOpenOrdersResult =
  | { ok: true; orders: PolyRelayOpenOrder[] }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

export async function listPolyOpenOrdersViaRelay(): Promise<ListPolyOpenOrdersResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/orders', { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { orders?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    if (!isRowObjectArray(body.orders)) {
      return { ok: false, status: res.status, error: 'malformed open-orders payload' };
    }
    return { ok: true, orders: body.orders as PolyRelayOpenOrder[] };
  }

  const code =
    body.code === 'NOT_IN_ORG' ||
    body.code === 'PERSONAL_KEY_MODE' ||
    body.code === 'NO_POLY_CRED' ||
    body.code === 'ONLY_LEGACY_EOA_CRED'
      ? body.code
      : undefined;

  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

export type CancelPolyOrderRelayResult =
  | { ok: true; result: unknown }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

export interface PolyFeeSchedule {
  feesEnabled: boolean;
  rate: number;
  exponent: number;
  takerOnly: boolean;
  rebateRate: number;
}

export type GetPolyFeeSchedulesResult =
  | { ok: true; schedules: Record<string, PolyFeeSchedule | null> }
  | { ok: false; status: number; error: string };

/** Batch the condition-id fee schedules used by trade preview and cash-out.
 * Null is an explicit unknown result from the relay, distinct from a known
 * fee-free schedule whose `feesEnabled` is false. */
export async function getPolyFeeSchedulesViaRelay(
  conditionIds: string[],
): Promise<GetPolyFeeSchedulesResult> {
  const unique = [...new Set(conditionIds.map((id) => id.trim()).filter(Boolean))];
  if (unique.length === 0) return { ok: true, schedules: {} };
  let res: Response;
  try {
    const qs = new URLSearchParams({ condition_ids: unique.join(',') });
    res = await apiFetch(`/api/polymarket/fee-schedules?${qs.toString()}`, { method: 'GET' });
  } catch (err) {
    return {
      ok: false,
      status: 0,
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
  let body: { schedules?: unknown; error?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }
  if (res.ok && body.schedules && typeof body.schedules === 'object') {
    return {
      ok: true,
      schedules: body.schedules as Record<string, PolyFeeSchedule | null>,
    };
  }
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket fee schedule failed (${res.status})`,
  };
}

// ── Read endpoints: fills / positions / pnl / realized ─────────
// Mirror listPolyOpenOrdersViaRelay's shape so all read helpers behave
// the same: discriminated union with optional `code` on unavailable
// states. Defer-note item (fills/positions/pnl) was deferred when only
// orders shipped; these helpers + the corresponding hooks land it.

export interface PolyRelayFill {
  /** Immutable local ledger row id (fills.id UUID). The required display
   *  identity: fill_id is nullable for legacy rows, and price/size/time/market
   *  cannot key a row because two distinct fills can look identical. */
  ledger_id: string;
  fill_id: string | null;
  order_id: string | null;
  /** Outcome token id (fills.asset_id, V20). null on pre-V20 rows. */
  asset_id: string | null;
  ticker: string;
  /** Relay-persisted market identity (poly_market_labels, V21) — survives
   *  markets the browser never saw live. Null when never captured. */
  market_label: string | null;
  market_sport: string | null;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  count: number;
  price_cents: number;
  is_taker: boolean;
  fees_cents: number | null;
  executed_at: string;
}

export type ListPolyFillsResult =
  | { ok: true; fills: PolyRelayFill[]; credentialContext?: PolyReadCredentialContext }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

const nonBlankString = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';
const nullableString = (v: unknown): v is string | null => v === null || typeof v === 'string';
const finiteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Strict row contract for the fills tape (#457). `isRowObjectArray` only proves
 *  the body is an array of objects, so `[{}]` used to become display state — and
 *  a tape row with no identity or no time cannot be ordered or deduplicated.
 *  Zero is accepted everywhere it is real data (0¢ marks, 0 fees); nothing is
 *  coerced, because a coerced 0 reads as a genuine $0.00 to the trader. */
function isPolyRelayFill(row: Record<string, unknown>): boolean {
  return (
    nonBlankString(row.ledger_id) &&
    nonBlankString(row.ticker) &&
    nullableString(row.fill_id) &&
    nullableString(row.order_id) &&
    nullableString(row.asset_id) &&
    nullableString(row.market_label) &&
    nullableString(row.market_sport) &&
    (row.side === 'yes' || row.side === 'no') &&
    (row.action === 'buy' || row.action === 'sell') &&
    typeof row.is_taker === 'boolean' &&
    finiteNumber(row.count) &&
    row.count >= 0 &&
    finiteNumber(row.price_cents) &&
    row.price_cents >= 0 &&
    row.price_cents <= 100 &&
    (row.fees_cents === null || finiteNumber(row.fees_cents)) &&
    typeof row.executed_at === 'string' &&
    Number.isFinite(Date.parse(row.executed_at))
  );
}

export async function listPolyFillsViaRelay(limit?: number): Promise<ListPolyFillsResult> {
  const qs = limit && limit > 0 ? `?limit=${encodeURIComponent(String(limit))}` : '';
  let res: Response;
  try {
    res = await apiFetch(`/api/polymarket/fills${qs}`, { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { fills?: unknown; credentialContext?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    // Whole-payload rejection, not per-row filtering: a partially dropped tape
    // is indistinguishable from a complete one, so the caller would render a
    // silently short history as if it were the trader's full recent activity.
    if (!isRowObjectArray(body.fills) || !body.fills.every(isPolyRelayFill)) {
      return { ok: false, status: res.status, error: 'malformed fills payload' };
    }
    return {
      ok: true,
      fills: body.fills as unknown as PolyRelayFill[],
      ...(body.credentialContext === undefined
        ? {}
        : { credentialContext: parseReadCredentialContext(body.credentialContext) }),
    };
  }

  const code = parseUnavailableCode(body.code);
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

/** Bounded per-market accounting rows from position_states. Unlike /fills,
 * this payload is complete regardless of fill-history pagination and carries
 * settlement state for positions held to resolution. */
export interface PolyRelayRealizedBucket {
  mergedCount?: number;
  mergeConflictCount?: number;
  splitCount?: number;
  splitAddedCostCents?: number;
  splitConflictCount?: number;
  splitFeeUnknownCount?: number;
  ticker: string;
  side: 'yes' | 'no';
  market_label: string | null;
  market_sport: string | null;
  netCount: number;
  totalBoughtCount: number;
  totalBuyCostCents: number;
  totalSoldCount: number;
  totalSellRevCents: number;
  heldCostBasisCents: number;
  runningRealizedPnlGrossCents: number;
  buyFeesActualCents: number;
  sellFeesActualCents: number;
  hasAnyActualBuyFee: boolean;
  hasAnyActualSellFee: boolean;
  buyFillCount?: number;
  sellFillCount?: number;
  buyFeeKnownFillCount?: number;
  sellFeeKnownFillCount?: number;
  isStillOpen: boolean;
  settlement: 'yes' | 'no' | 'void' | null;
  settledAt: string | null;
  lastFillAt: string;
}

export type ListPolyRealizedResult =
  | { ok: true; buckets: PolyRelayRealizedBucket[]; merges: PolyMergeAccounting[] | null; credentialContext?: PolyReadCredentialContext }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

export async function listPolyRealizedViaRelay(): Promise<ListPolyRealizedResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/realized', { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { buckets?: unknown; credentialContext?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    if (!isRowObjectArray(body.buckets)) {
      return {
        ok: false,
        status: res.status,
        error: 'malformed realized-history payload',
      };
    }
    return {
      ok: true,
      buckets: body.buckets as unknown as PolyRelayRealizedBucket[],
      merges: decodePolyMerges(body),
      ...(body.credentialContext === undefined
        ? {}
        : { credentialContext: parseReadCredentialContext(body.credentialContext) }),
    };
  }

  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code: parseUnavailableCode(body.code),
  };
}

export interface PolyRelayPosition {
  /** A conflicted merge makes this position unconfirmed, never a Close target. */
  mergeConflictCount?: number;
  splitCount?: number;
  splitAddedCostCents?: number;
  splitConflictCount?: number;
  splitFeeUnknownCount?: number;
  ticker: string;
  side: 'yes' | 'no';
  netCount: number;
  avgEntryPriceCents: number;
  /** Canonical most-recent fill applied to this aggregate. */
  lastFillAt?: string | null;
  /** Exact basis of currently-held contracts in cents (un-rounded). Prefer
   *  this over avgEntryPriceCents × netCount — the avg is whole-cent
   *  quantized and re-multiplying it drifts on fractional/many-fill
   *  positions (the I-035 class the Kalshi path already fixed). Optional
   *  only for old cached payloads; the relay always sends it. */
  costBasisCents?: number;
  realizedPnlCents: number;
  feesPaidCents?: number;
  /** Opening fees on the OPEN share only — the unrealized-recipe fee input. */
  openFeesCents?: number;
  /** Lifetime bought / sold contracts — the full-position anchor (#91). Cards
   *  derive original qty and its cost/to-win from these; netCount is the
   *  post-sale remainder and must never stand in for the original. Optional
   *  only for old cached payloads; the relay always sends them. */
  totalBoughtCount?: number;
  totalSoldCount?: number;
  /** Lifetime buy cost in cents — the original entry basis, un-rounded.
   *  Fee-exclusive (see the relay's OpenPosition jsdoc). */
  totalBuyCostCents?: number;
}

export type ListPolyPositionsResult =
  | { ok: true; positions: PolyRelayPosition[]; credentialContext?: PolyReadCredentialContext }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode; accountingConflict?: boolean };

export async function listPolyPositionsViaRelay(): Promise<ListPolyPositionsResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/positions', { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { positions?: unknown; credentialContext?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    if (!isRowObjectArray(body.positions)) {
      return { ok: false, status: res.status, error: 'malformed positions payload' };
    }
    if (body.positions.some((row) => Number(row.mergeConflictCount ?? 0) > 0 || Number(row.splitConflictCount ?? 0) > 0)) {
      return { ok: false, status: 409, accountingConflict: true, error: 'Polymarket position accounting conflict. Quantities and Close controls are withheld until reconciliation.' };
    }
    // Through unknown: the guard narrows to Record<string, unknown>[], which
    // does not structurally overlap PolyRelayPosition (required fields, no
    // index signature). Field-level trust is unchanged from the old cast.
    return {
      ok: true,
      positions: body.positions as unknown as PolyRelayPosition[],
      ...(body.credentialContext === undefined
        ? {}
        : { credentialContext: parseReadCredentialContext(body.credentialContext) }),
    };
  }

  const code = parseUnavailableCode(body.code);
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

export interface PolyRelayPnl {
  realizedPnlCents: number;
  /** Recorded fees attributable to realized shares. Null means fee coverage
   *  is incomplete, not that the known fee is zero. */
  realizedFeesCents: number | null;
  /** Realized P&L after recorded fees. Null when fee coverage is incomplete. */
  realizedPnlNetCents: number | null;
  openCount: number;
  openNotionalCents: number;
}

export type GetPolyPnlResult =
  | { ok: true; pnl: PolyRelayPnl; credentialContext?: PolyReadCredentialContext }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Fail-closed decoder for the documented /pnl response (#436). All five keys
 *  must be present without coercion; the fee pair is both-number (known) or
 *  both-null (unknown coverage) — the relay never emits a mixed pair. Extra
 *  metadata (settlements) is tolerated; failure-envelope markers are not, so
 *  an error body can't hide behind placeholder zero metrics. */
function isPolyRelayPnlPayload(
  value: unknown,
): value is PolyRelayPnl & { credentialContext?: unknown } {
  if (!isObjectRecord(value)) return false;
  if (
    hasOwn(value, 'error') ||
    hasOwn(value, 'code') ||
    value.ok === false ||
    value.success === false
  ) {
    return false;
  }

  const requiredKeys = [
    'realizedPnlCents',
    'realizedFeesCents',
    'realizedPnlNetCents',
    'openCount',
    'openNotionalCents',
  ];
  if (requiredKeys.some((key) => !hasOwn(value, key))) return false;

  if (
    !isFiniteNumber(value.realizedPnlCents) ||
    !isFiniteNumber(value.openCount) ||
    !isFiniteNumber(value.openNotionalCents)
  ) {
    return false;
  }

  const feesKnown =
    isFiniteNumber(value.realizedFeesCents) &&
    isFiniteNumber(value.realizedPnlNetCents);
  const feesUnknown =
    value.realizedFeesCents === null &&
    value.realizedPnlNetCents === null;
  return feesKnown || feesUnknown;
}

export async function getPolyPnlViaRelay(): Promise<GetPolyPnlResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/pnl', { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    if (!isPolyRelayPnlPayload(body)) {
      return { ok: false, status: res.status, error: 'malformed pnl payload' };
    }
    return {
      ok: true,
      pnl: {
        realizedPnlCents: body.realizedPnlCents,
        realizedFeesCents: body.realizedFeesCents,
        realizedPnlNetCents: body.realizedPnlNetCents,
        openCount: body.openCount,
        openNotionalCents: body.openNotionalCents,
      },
      ...(body.credentialContext === undefined
        ? {}
        : { credentialContext: parseReadCredentialContext(body.credentialContext) }),
    };
  }

  const errorBody = isObjectRecord(body) ? body : {};
  const code = parseUnavailableCode(
    typeof errorBody.code === 'string' ? errorBody.code : undefined,
  );
  return {
    ok: false,
    status: res.status,
    error:
      typeof errorBody.error === 'string'
        ? errorBody.error
        : `Polymarket relay failed (${res.status})`,
    code,
  };
}

function parseUnavailableCode(code: string | undefined): PolyRelayUnavailableCode | undefined {
  return code === 'NOT_IN_ORG' ||
    code === 'PERSONAL_KEY_MODE' ||
    code === 'NO_POLY_CRED' ||
    code === 'ONLY_LEGACY_EOA_CRED'
    ? code
    : undefined;
}

function parseReadCredentialContext(value: unknown): PolyReadCredentialContext {
  if (value === null) return null;
  if (
    value === 'NOT_IN_ORG' ||
    value === 'PERSONAL_KEY_MODE' ||
    value === 'NO_POLY_CRED' ||
    value === 'ONLY_LEGACY_EOA_CRED' ||
    value === 'CONTEXT_CHECK_FAILED'
  ) {
    return value;
  }
  // A new frontend can briefly meet an older relay during a rolling deploy.
  // Preserve the ledger payload, but never translate missing/unknown advisory
  // metadata into a falsely healthy null.
  return 'CONTEXT_CHECK_FAILED';
}

// ── Post-deposit allowance refresh ─────────────────────────────
// Calls /api/polymarket/refresh-allowance which forces the CLOB to
// re-read on-chain balance + allowance for the org's stored credential
// triple and returns the fresh values. Required for sigType=3 (deposit
// wallet) accounts after every deposit; harmless for 0/1/2.

export interface PolyFunderBalance {
  collateralUsd: number;
  collateralAllowanceUsd: number;
}

export type RefreshPolyAllowanceResult =
  | { ok: true; balance: PolyFunderBalance }
  /** #1205: refreshed, but a manager hid the organization balance. */
  | { ok: true; balance: null; hidden: true }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode };

export type GetPolyBalanceResult =
  | { ok: true; balance: PolyFunderBalance }
  | { ok: false; status: number; error: string; code?: PolyRelayUnavailableCode }
  /** #1205: a manager hid the organization balance from this trader. */
  | { ok: false; status: number; error: string; hidden: true };

/**
 * Org-key funder balance via GET /api/polymarket/balance — the relay
 * resolves the org's credential pool (falls through keys on auth-style
 * failures). Returns an unavailable code when the org has no poly creds;
 * callers fall back to the personal polyAccountStore balance in that case
 * ("org key enabled → org key" resolution order).
 */
export async function getPolyBalanceViaRelay(): Promise<GetPolyBalanceResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/balance', { method: 'GET' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { balance?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    const b = (body.balance ?? {}) as Record<string, unknown>;
    return {
      ok: true,
      balance: {
        collateralUsd: Number(b.collateralUsd) || 0,
        collateralAllowanceUsd: Number(b.collateralAllowanceUsd) || 0,
      },
    };
  }

  if (isOrgBalanceHiddenBody(body)) {
    return { ok: false, status: res.status, error: body.error ?? 'Organization balances are hidden', hidden: true };
  }
  const code = parseUnavailableCode(body.code);
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

export async function refreshPolyAllowanceViaRelay(): Promise<RefreshPolyAllowanceResult> {
  let res: Response;
  try {
    res = await apiFetch('/api/polymarket/refresh-allowance', { method: 'POST' });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { balance?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  // #1205: the refresh ran, but a manager hid the figure it read back.
  if (res.ok && isOrgBalanceHiddenBody(body)) return { ok: true, balance: null, hidden: true };
  if (res.ok) {
    const b = (body.balance ?? {}) as Record<string, unknown>;
    return {
      ok: true,
      balance: {
        collateralUsd: Number(b.collateralUsd) || 0,
        collateralAllowanceUsd: Number(b.collateralAllowanceUsd) || 0,
      },
    };
  }

  const code = parseUnavailableCode(body.code);
  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}

/**
 * #1020 — what the CLOB said about ONE order in a cancel response. The venue
 * answers `{ canceled: string[], not_canceled: { [orderId]: reason } }`; a 2xx
 * alone does not mean the order is gone (it may have been matched first).
 * Only `canceled` proves it; anything else is not a basis to act as if it were.
 */
export type PolyCancelOutcome =
  | { kind: 'canceled' }
  | { kind: 'not_canceled'; reason: string }
  | { kind: 'unknown' };

export function polyCancelOutcome(result: unknown, orderId: string): PolyCancelOutcome {
  if (!result || typeof result !== 'object') return { kind: 'unknown' };
  const r = result as { canceled?: unknown; not_canceled?: unknown };
  const target = orderId.toLowerCase();
  if (Array.isArray(r.canceled) && r.canceled.some((id) => typeof id === 'string' && id.toLowerCase() === target)) {
    return { kind: 'canceled' };
  }
  if (r.not_canceled && typeof r.not_canceled === 'object') {
    for (const [id, reason] of Object.entries(r.not_canceled as Record<string, unknown>)) {
      if (id.toLowerCase() === target) return { kind: 'not_canceled', reason: typeof reason === 'string' ? reason : 'not canceled' };
    }
  }
  return { kind: 'unknown' };
}

export async function cancelPolyOrderViaRelay(
  orderId: string,
): Promise<CancelPolyOrderRelayResult> {
  let res: Response;
  try {
    res = await apiFetch(`/api/polymarket/orders/${encodeURIComponent(orderId)}`, {
      method: 'DELETE',
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, error: message };
  }

  let body: { result?: unknown; error?: string; code?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* non-JSON body */
  }

  if (res.ok) {
    return { ok: true, result: body.result };
  }

  const code =
    body.code === 'NOT_IN_ORG' ||
    body.code === 'PERSONAL_KEY_MODE' ||
    body.code === 'NO_POLY_CRED' ||
    body.code === 'ONLY_LEGACY_EOA_CRED'
      ? body.code
      : undefined;

  return {
    ok: false,
    status: res.status,
    error: body.error ?? `Polymarket relay failed (${res.status})`,
    code,
  };
}
